import { HttpParams } from "@angular/common/http";
import { MovidaRegisterType, SortType } from "../enums/commons.enum";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { IFilter, IForm, IModel, IModelConstructor, IMovidaBadgeItem, IMovidaLanguage, IMovidaMenuItem } from "./abstractions.model";
import { MovidaValidators } from "../validators/movida.validators";

// Strings
export class StringHelpers {
	public static format(format: any, ...args: any) {
		for (let i = 0, l = args.length; i < l; i++) {
			format = format.replace(new RegExp('\\{' + i + '\\}', 'gm'), args[i]);
		}
		return format;
	}
}

// Date
export class PartialDate implements IModel, IForm {
	form?: UntypedFormGroup;

	day?: number;
	month?: number;
	year?: number;

	minimumYear?: number;
	maximumYear?: number;

	constructor(other?: any, minimumYear?: number, maximumYear?: number) {
		this.minimumYear = minimumYear != null ? minimumYear : -5000;
		this.maximumYear = maximumYear != null ? maximumYear : 5000;

		if (other != null) {
			this.day = other.day;
			this.month = other.month;
			this.year = other.year;
		}
	}

	toString() {
		if (this.day != null && this.month != null && this.year != null)
			return StringHelpers.format("{0}/{1}/{2}", this.day.toString().padStart(2, '0'), this.month.toString().padStart(2, '0'), this.year);
		else if (this.month != null && this.year != null)
			return StringHelpers.format("{1}/{2}", this.month.toString().padStart(2, '0'), this.year);
		else if (this.year != null)
			return this.year.toString();

		return "";
	}

	applyDate(year?: number | null, month?: number | null, day?: number | null) {
		let monthDays = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
		if (year != null) {
			this.year = year;
			if (month != null && month > 0 && month <= 12) {
				this.month = month;
				if (day != null) {
					monthDays[2] += (year % 4 == 0) ? (year % 100 == 0) ? (year % 400 == 0) ? 1 : 0 : 1 : 0;
					if (day > 0 && day <= monthDays[this.month])
						this.day = day;
				}
			}
		}

	}

	get asForm(): UntypedFormGroup {
		if (this.form == null) {
			this.form = new UntypedFormGroup({
				day: new UntypedFormControl(this.day, [Validators.min(1), Validators.max(31)]),
				month: new UntypedFormControl(this.month),
				year: new UntypedFormControl(this.year, [MovidaValidators.numberNullableRange(this.minimumYear ?? 1970, this.maximumYear ?? 2030)])
			});
		}
		return this.form;
	}

	get fromForm(): PartialDate {
		let value = this.form?.value;
		let y = value.year != null ? parseInt(value.year) : null;
		let m = value.month != null ? parseInt(value.month) : null;
		let d = value.day != null ? parseInt(value.day) : null;

		this.applyDate(y, m, d);

		return this;
	}
}

// Error
export class ErrorItem {
	code: number = 0;
	message: string = "";
	field?: string = undefined;

	constructor(other?: Array<string> | ErrorItem | { code: number, message: string, field?: string } | string) {
		if (other != null) {
			if (typeof other == "string") {
				this.message = other;
			} else if (typeof other == "object") {
				if (other instanceof Array) {
					if (other.length > 0) this.code = parseInt(other[0]);
					if (other.length > 1) this.message = other[1];
					if (other.length > 2) this.field = other[2];
				} else {
					this.code = other.code;
					this.message = other.message;
					this.field = other.field;
				}
			}
		}
	}
}
export class ErrorList {
	errors: Array<ErrorItem>;

	get error(): ErrorItem | undefined { return (this.errors.length > 0) ? this.errors[0] : undefined; }

	constructor(other?: any) {
		this.errors = new Array<ErrorItem>();
		if (other != null) {
			if (typeof other == "string") {
				this.errors.push(new ErrorItem({ code: -1, message: other, field: "" }));
			} else if (other.errors instanceof Array && other.errors.length > 0) {
				for (let i = 0, l = other.errors.length; i < l; i++)
					this.errors.push(new ErrorItem(other.errors[i]));
			}
		}
	}
}

// Pagination
export class Pagination {
	page: number = 1;
	pageSize: number = 20;
	field?: string;
	sort?: SortType;

	constructor(other?: number | { page: number, pageSize: number, field: string | undefined, sort: SortType } | Pagination, pageSize: number = 20, field: string | undefined = undefined, sort: SortType = SortType.Ascending) {
		if (other != null) {
			if (typeof other === "object") {
				this.page = other.page || 1;
				this.pageSize = other.pageSize || 20;
				this.field = other.field || undefined;
				this.sort = other.sort || undefined;
			} else {
				this.page = other;
				this.pageSize = pageSize;
				this.field = field;
				this.sort = sort;
			}
		}
	}

	toHttpParams(): HttpParams {
		let params = new HttpParams()
			.set('page', this.page.toString())
			.set('pageSize', this.pageSize.toString())

		if (this.field != undefined)
			params.set('field', this.field);

		if (this.sort != undefined)
			params.set('sort', this.sort.toString());

		return params;
	}
}
export class FilterPagination extends Pagination {
	filters?: IFilter;

	constructor(other?: any,
		filters?: IFilter,
		pageSize: number = 20,
		field?: string,
		sort: SortType = SortType.Ascending) {
		super(other, pageSize, field, sort);

		this.filters = undefined;
		if (filters != undefined)
			this.filters = filters;
	}
}
export class PaginatedList<TModel extends IModel> {
	data?: Array<TModel>;
	nextPageToken: string = "";
	currentPage: number = 1;
	pageCount: number = 1;
	totalCount: number = 0;
	pageSize: number = 20;

	constructor(ctor: IModelConstructor<TModel>, list: any) {
		if (list != null) {
			this.nextPageToken = (list.hasOwnProperty('nextPageToken')) ? list.nextPageToken : "";
			this.currentPage = (list.hasOwnProperty('currentPage')) ? list.currentPage : 1;
			this.pageCount = (list.hasOwnProperty('pageCount')) ? list.pageCount : 1;
			this.totalCount = (list.hasOwnProperty('totalCount')) ? list.totalCount : 0;
			this.pageSize = (list.hasOwnProperty('pageSize')) ? list.pageSize : 20;

			if (list.hasOwnProperty('data') && list.data instanceof Array) {
				this.data = new Array<TModel>();
				for (let value of list.data)
					this.data.push(new ctor(value));
			}
		}
	}

	public mapData(key: string = "id"): Map<number, TModel> {
		let map = new Map<number, TModel>();
		this.data?.forEach((x: any) => map.set(x[key], x));
		return map;
	}

	public mapDataString(key: string = "id", name: string = "name"): Map<string, string> {
		let map = new Map<string, string>();
		this.data?.forEach((x: any) => map.set(x[key], x[name]));
		return map;
	}
}

/// Query
export class MovidaQueryFilter extends FilterPagination {
	constructor(other?: any, filters?: IFilter,
		pageSize: number = 20, field: string | undefined = undefined,
		sort: SortType = SortType.Ascending) {
		super(other, filters, pageSize, field, sort);
	}
}
export class MovidaQueryMessage {
	public empty: string = "";
	public total: string = "";
	public remove: string = "";

	constructor(other?: Array<string> | MovidaQueryMessage | { empty: string, total: string, remove: string }) {
		if (other != null) {
			if (other instanceof Array) {
				if (other.length > 0) this.empty = other[0];
				if (other.length > 1) this.total = other[1];
				if (other.length > 2) this.remove = other[2];
			} else {
				this.empty = other.empty || "";
				this.total = other.total || "";
				this.remove = other.remove || "";
			}
		}
	}
}
export class MovidaQueryColumn {
	public name: string = "";
	public prop: string = "";
	public flexGrow: number = 100;

	constructor(other: MovidaQueryColumn | { name: string, prop: string, flexGrow: number }) {
		if (other != null) {
			this.name = other.name || "";
			this.prop = other.prop || "";
			this.flexGrow = other.flexGrow || 100;
		}
	}
}

// Register
export class MovidaRegisterString {
	public new: string = "";
	public edit: string = "";

	constructor(other?: Array<string> | { new: string, edit: string }, edit?: string) {
		if (other != null) {
			if (other instanceof Array) {
				if (other.length > 0) this.new = other[0];
				if (other.length > 1) this.edit = other[1];
			} else {
				this.new = other.new || "";
				this.edit = other.edit || "";
			}
		}
	}

	get(type: MovidaRegisterType) {
		return (type == MovidaRegisterType.Edit) ? this.edit : this.new;
	}
}

// Identifiable
export abstract class GenericIdentifiable<T> {
	id?: T;

	constructor(other?: any) {
		if (other != undefined) {
			if (typeof other === "object" && other.hasOwnProperty('id'))
				this.id = other.id;
		}
	}
}
export class Identifiable extends GenericIdentifiable<number> {
	constructor(other?: any) {
		super(other);
		if (typeof other === "number")
			this.id = other;
	}
}
export class IdentifiableString extends GenericIdentifiable<string> {
	constructor(other?: any) {
		super(other);
		if (typeof other === "string")
			this.id = other;
	}
}
export class GenericAuditable<T> extends GenericIdentifiable<T> {
	createdDate?: Date;
	updatedDate?: Date;
	createdUserId?: T;
	updatedUserId?: T;

	constructor(other?: any) {
		super(other);
		if (other != undefined) {
			this.createdDate = other.createdDate != undefined ? new Date(other.createdDate) : undefined;
			this.updatedDate = other.updatedDate != undefined ? new Date(other.updatedDate) : undefined;
		}
	}
}
export class Auditable extends GenericAuditable<number> {
	constructor(other?: any) {
		super(other);
	}
}
export class AuditableString extends GenericAuditable<string> {
	constructor(other?: any) {
		super(other);
	}
}

// Localization
export class LoganLanguage implements IMovidaLanguage {
	id: number;
	name: string;
	flag: string;
	language: string;

	constructor(other?: IMovidaLanguage | number, name?: string, flag?: string, language?: string) {
		this.id = 0;
		this.name = "";
		this.flag = "";
		this.language = "";

		if (other != undefined) {
			if (typeof other === "number") {
				this.id = other;
				this.name = name ?? "";
				this.flag = flag ?? "";
				this.language = language ?? "";
			} else if (typeof other === "object") {
				this.id = other.id;
				this.name = other.name;
				this.flag = other.flag;
				this.language = other.language;
			}
		}
	}

	public static build(items: Array<any>): Array<LoganLanguage> {
		let list = new Array<LoganLanguage>();

		items.forEach((x: any) => {
			list.push(LoganLanguage.create(x));
		});

		return list;
	}

	public static buildMap(items: Array<any>): Map<string, LoganLanguage> {
		let map = new Map<string, LoganLanguage>();

		items.forEach((x: any) => {
			let m = LoganLanguage.create(x);
			map.set(m.language, m);
		});

		return map;
	}

	private static create(item: any): LoganLanguage {
		let value = new LoganLanguage();

		if (item.id != undefined) value.id = item.id;
		if (item.name != undefined) value.name = item.name;
		if (item.flag != undefined) value.flag = item.flag;
		if (item.language != undefined) value.language = item.language;

		return value;
	}
}



// Menu
export class MovidaBadgeItem implements IMovidaBadgeItem {
	text: string;
	variant: string;

	constructor(other?: IMovidaBadgeItem | string, variant?: string) {
		this.text = "";
		this.variant = "";

		if (other != undefined) {
			if (typeof other === "string") {
				this.text = other;
				this.variant = variant ?? "";
			} else if (typeof other === "object") {
				this.text = other.text;
				this.variant = other.variant;
			}
		}
	}
}

export class MovidaMenuItem implements IMovidaMenuItem {
	id: number;
	label: string;
	icon?: string;
	link?: string;
	isTitle: boolean;
	isLayout: boolean;
	parentId?: number;
	badge?: IMovidaBadgeItem;
	children?: Array<IMovidaMenuItem>;

	constructor(other?: IMovidaMenuItem | number, label?: string, icon?: string, link?: string, isTitle?: boolean, isLayout?: boolean, parentId?: number, badge?: IMovidaBadgeItem, children?: Array<IMovidaMenuItem>) {
		this.id = 0;
		this.label = "";
		this.isTitle = false;
		this.isLayout = false;

		if (other != undefined) {
			if (typeof other === "number") {
				this.id = other;
				this.label = label ?? "";
				this.icon = icon;
				this.link = link;
				this.isTitle = isTitle ?? false;
				this.isLayout = isLayout ?? false;
				this.parentId = parentId;
				this.badge = new MovidaBadgeItem(badge);
				if (children != undefined && children.length > 0) {
					this.children = new Array<IMovidaMenuItem>();
					this.children = children.map((x: IMovidaMenuItem) => new MovidaMenuItem(x));
				}

			} else if (typeof other === "object") {
				this.id = other.id;
				this.label = other.label;
				this.icon = other.icon;
				this.link = other.link;
				this.isTitle = other.isTitle ?? false;
				this.isLayout = other.isLayout ?? false;
				this.parentId = other.parentId;
				this.badge = new MovidaBadgeItem(other.badge);
				if (children != undefined && children.length > 0) {
					this.children = new Array<IMovidaMenuItem>();
					this.children = children.map((x: IMovidaMenuItem) => new MovidaMenuItem(x));
				}
			}
		}
	}

	public static build(items: Array<any>): Array<MovidaMenuItem> {
		let list = new Array<MovidaMenuItem>();

		items.forEach((x: any) => {
			list.push(MovidaMenuItem.create(x));
		});
		return list;
	}

	private static create(item: any): MovidaMenuItem {
		let value = new MovidaMenuItem();

		if (item.id != undefined) value.id = item.id;
		if (item.label != undefined) value.label = item.label;
		if (item.icon != undefined) value.icon = item.icon;
		if (item.link != undefined) value.link = item.link;
		if (item.isTitle != undefined) value.isTitle = item.isTitle;
		if (item.isLayout != undefined) value.isLayout = item.isLayout;
		if (item.parentId != undefined) value.parentId = item.parentId;
		if (item.badge != undefined) value.badge = item.badge;
		if (item.children != undefined) value.children = MovidaMenuItem.build(item.children);

		return value;
	}
}
